import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Power, KeyRound, Trash2, Eye, EyeOff, ArrowRight, UserPlus } from "lucide-react";
import api, { apiError, fmtDate } from "../api";
import { Card, PageHeader, Button, Input, Textarea, Select, Field, Modal, Table, EmptyState, Spinner, Badge } from "../components/ui";

const TABS = [
  { key: "client", label: "Clients" },
  { key: "clinic", label: "Clinics" },
  { key: "pandi", label: "P&I" },
  { key: "staff", label: "Doctors & Accounts" },
];

const ORG_LABELS = { client: "Client", clinic: "Clinic", pandi: "P&I Club" };

const ALL_DOC_CATEGORIES = [
  ["declaration", "Candidate Declaration Form"],
  ["ilo_page", "ILO Page"],
  ["medical_reports", "Medical Reports"],
  ["consent_form", "MedVerify Consent Form"],
  ["additional_tests", "Prescription / Additional Tests"],
];
const ALL_DOC_KEYS = ALL_DOC_CATEGORIES.map(([k]) => k);

function OrgModal({ open, onClose, type, org, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", address: "", description: "", visible_doc_categories: ALL_DOC_KEYS });
  const [busy, setBusy] = useState(false);
  const orgType = org ? org.type : type;
  const isClient = orgType === "client";
  const label = ORG_LABELS[orgType] || "Organization";

  useEffect(() => {
    setForm(org
      ? { name: org.name, email: org.email, address: org.address, description: org.description || "", visible_doc_categories: org.visible_doc_categories ?? ALL_DOC_KEYS }
      : { name: "", email: "", address: "", description: "", visible_doc_categories: ALL_DOC_KEYS });
  }, [org, open]);

  const toggleCat = (key) => setForm((f) => ({
    ...f,
    visible_doc_categories: f.visible_doc_categories.includes(key)
      ? f.visible_doc_categories.filter((k) => k !== key)
      : [...f.visible_doc_categories, key],
  }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = isClient ? form : { name: form.name, email: form.email, address: form.address, description: form.description };
      if (org) await api.put(`/admin/organizations/${org.id}`, payload);
      else await api.post("/admin/organizations", { ...payload, type });
      toast.success(org ? `${label} updated` : `${label} created`);
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title={org ? `Edit ${label}` : `Add ${ORG_LABELS[type] || "Organization"}`}>
      <form onSubmit={submit} className="space-y-4">
        <Field label={`${label} Name`} required>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="org-name-input" />
        </Field>
        <Field label="Email">
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="org-email-input" />
        </Field>
        <Field label="Address" required={isClient}>
          <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} required={isClient} data-testid="org-address-input" />
        </Field>
        {isClient && (
          <Field label="Description">
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="org-description-input" />
          </Field>
        )}
        {isClient && (
          <Field label="Documents visible in Client portal">
            <div className="space-y-2 rounded-md border border-slate-200 p-3">
              {ALL_DOC_CATEGORIES.map(([key, catLabel]) => (
                <label key={key} className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 accent-teal"
                    checked={form.visible_doc_categories.includes(key)}
                    onChange={() => toggleCat(key)}
                    data-testid={`org-doccat-${key}`}
                  />
                  {catLabel}
                </label>
              ))}
              <p className="text-xs text-slate-400">Only ticked documents will be visible to this client's users.</p>
            </div>
          </Field>
        )}
        <Button type="submit" className="w-full" disabled={busy} data-testid="org-save-btn">
          {busy ? "Saving..." : org ? "Save Changes" : "Create Organization"}
        </Button>
      </form>
    </Modal>
  );
}

export function StaffUserModal({ open, onClose, onSaved, fixedRole, orgId }) {
  const [form, setForm] = useState({ full_name: "", username: "", password: "", confirm: "", email: "", role: fixedRole || "doctor" });
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setForm({ full_name: "", username: "", password: "", confirm: "", email: "", role: fixedRole || "doctor" });
  }, [open, fixedRole]);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/admin/users", {
        full_name: form.full_name, username: form.username, password: form.password,
        email: form.email.trim() || (form.username.includes("@") ? form.username : ""),
        role: form.role, organization_id: orgId || null,
      });
      toast.success("User created");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Add User">
      <form onSubmit={submit} className="space-y-4">
        {!fixedRole && (
          <Field label="Role" required>
            <Select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="user-role-select">
              <option value="doctor">Doctor</option>
              <option value="accounts">Accounts</option>
              <option value="admin">Admin</option>
            </Select>
          </Field>
        )}
        <Field label="Full Name">
          <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="user-fullname-input" />
        </Field>
        <Field label="Username" required>
          <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required minLength={3} data-testid="user-username-input" />
        </Field>
        <Field label="Email">
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
        </Field>
        <Field label="Password" required>
          <div className="relative">
            <Input type={show ? "text" : "password"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} data-testid="user-password-input" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600" data-testid="user-toggle-password">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Field>
        <Field label="Confirm Password" required>
          <Input type={show ? "text" : "password"} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} required data-testid="user-confirm-password-input" />
        </Field>
        <Button type="submit" className="w-full" disabled={busy} data-testid="user-save-btn">
          {busy ? "Creating..." : "Create User"}
        </Button>
      </form>
    </Modal>
  );
}

export function ResetPasswordModal({ open, onClose, targetUser }) {
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setPw(""); setConfirm(""); }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (pw !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post(`/admin/users/${targetUser.id}/reset-password`, { new_password: pw });
      toast.success(`Password reset for ${targetUser.username}`);
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  if (!targetUser) return null;
  return (
    <Modal open={open} onClose={onClose} title={`Reset Password — ${targetUser.full_name}`}>
      <form onSubmit={submit} className="space-y-4">
        <Field label="New Password" required>
          <div className="relative">
            <Input type={show ? "text" : "password"} value={pw} onChange={(e) => setPw(e.target.value)} required minLength={6} data-testid="reset-password-input" />
            <button type="button" onClick={() => setShow(!show)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
              {show ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
        </Field>
        <Field label="Confirm Password" required>
          <Input type={show ? "text" : "password"} value={confirm} onChange={(e) => setConfirm(e.target.value)} required data-testid="reset-confirm-password-input" />
        </Field>
        <Button type="submit" className="w-full" disabled={busy} data-testid="reset-password-submit-btn">
          {busy ? "Resetting..." : "Reset Password"}
        </Button>
      </form>
    </Modal>
  );
}

function EditUserModal({ open, onClose, targetUser, onSaved }) {
  const [form, setForm] = useState({ full_name: "", username: "", email: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (targetUser) setForm({ full_name: targetUser.full_name || "", username: targetUser.username || "", email: targetUser.email || "" });
  }, [targetUser, open]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put(`/admin/users/${targetUser.id}`, {
        full_name: form.full_name, username: form.username,
        email: form.email.trim() || (form.username.includes("@") ? form.username : ""),
      });
      toast.success("User updated");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(apiError(err));
    } finally { setBusy(false); }
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit User">
      <form onSubmit={submit} className="space-y-4">
        <Field label="Full Name">
          <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="edit-user-fullname-input" />
        </Field>
        <Field label="Username" required>
          <Input value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required minLength={3} data-testid="edit-user-username-input" />
        </Field>
        <Field label="Email">
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="edit-user-email-input" />
        </Field>
        <Button type="submit" className="w-full" disabled={busy} data-testid="edit-user-save-btn">
          {busy ? "Saving..." : "Save Changes"}
        </Button>
      </form>
    </Modal>
  );
}

export function UsersTable({ users, onChanged, roleColumn = true }) {
  const [resetTarget, setResetTarget] = useState(null);
  const [editTarget, setEditTarget] = useState(null);
  const toggle = async (u) => {
    try {
      await api.put(`/admin/users/${u.id}`, { status: u.status === "active" ? "inactive" : "active" });
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.username}?`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("User deleted");
      onChanged();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <>
      <Table headers={["Full Name", "Username", roleColumn ? "Role" : "Email", "Status", "Created", "Actions"]}>
        {users.map((u) => (
          <tr key={u.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50" data-testid="user-row">
            <td className="px-4 py-3 font-semibold text-slate-900">{u.full_name}</td>
            <td className="px-4 py-3 font-mono text-slate-600">{u.username}</td>
            {roleColumn
              ? <td className="px-4 py-3"><Badge color="navy">{u.role}</Badge></td>
              : <td className="px-4 py-3 text-slate-600">{u.email || "-"}</td>}
            <td className="px-4 py-3">
              <Badge color={u.status === "active" ? "green" : "red"}>{u.status}</Badge>
            </td>
            <td className="px-4 py-3 text-slate-500">{fmtDate(u.created_at)}</td>
            <td className="px-4 py-3">
              <div className="flex flex-wrap gap-1.5">
                <button onClick={() => setEditTarget(u)} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 transition-colors hover:border-navy hover:text-navy" data-testid={`edit-user-${u.username}`}>
                  <Pencil size={12} /> Edit
                </button>
                <button onClick={() => setResetTarget(u)} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 transition-colors hover:border-teal hover:text-teal" data-testid={`reset-pw-${u.username}`}>
                  <KeyRound size={12} /> Reset
                </button>
                <button onClick={() => toggle(u)} className={`inline-flex items-center gap-1 rounded-md border px-2 py-1 text-xs font-semibold transition-colors ${u.status === "active" ? "border-slate-200 text-slate-600 hover:border-amber-400 hover:text-amber-600" : "border-slate-200 text-slate-600 hover:border-emerald-400 hover:text-emerald-600"}`} data-testid={`toggle-user-${u.username}`}>
                  <Power size={12} /> {u.status === "active" ? "Deactivate" : "Activate"}
                </button>
                <button onClick={() => remove(u)} className="inline-flex items-center gap-1 rounded-md border border-slate-200 px-2 py-1 text-xs font-semibold text-slate-600 transition-colors hover:border-red-400 hover:text-red-600" data-testid={`delete-user-${u.username}`}>
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            </td>
          </tr>
        ))}
      </Table>
      <ResetPasswordModal open={!!resetTarget} onClose={() => setResetTarget(null)} targetUser={resetTarget} />
      <EditUserModal open={!!editTarget} onClose={() => setEditTarget(null)} targetUser={editTarget} onSaved={onChanged} />
    </>
  );
}

export default function AdminOrganizations() {
  const [tab, setTab] = useState("client");
  const [orgs, setOrgs] = useState(null);
  const [staff, setStaff] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editOrg, setEditOrg] = useState(null);
  const [staffModal, setStaffModal] = useState(false);

  const load = useCallback(() => {
    if (tab === "staff") {
      Promise.all([api.get("/admin/users", { params: { role: "doctor" } }), api.get("/admin/users", { params: { role: "accounts" } }), api.get("/admin/users", { params: { role: "admin" } })])
        .then(([d, a, ad]) => setStaff([...d.data, ...a.data, ...ad.data]))
        .catch(() => setStaff([]));
    } else {
      api.get("/admin/organizations", { params: { type: tab } }).then((r) => setOrgs(r.data)).catch(() => setOrgs([]));
    }
  }, [tab]);

  useEffect(() => { setOrgs(null); setStaff(null); load(); }, [load]);

  const toggleOrg = async (o) => {
    try {
      await api.put(`/admin/organizations/${o.id}`, { status: o.status === "active" ? "inactive" : "active" });
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const removeOrg = async (o) => {
    if (!window.confirm(`Delete ${o.name}? Its user accounts and services will be removed permanently. Existing candidate records stay but will no longer show this ${(ORG_LABELS[o.type] || "organization").toLowerCase()}.`)) return;
    try {
      await api.delete(`/admin/organizations/${o.id}`);
      toast.success(`${ORG_LABELS[o.type] || "Organization"} deleted`);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div>
      <PageHeader title="Organizations" subtitle="Manage clients, clinics, P&I clubs, doctors and accounts staff">
        <Link to="/candidates/new">
          <Button variant="outline" data-testid="admin-register-candidate-btn"><UserPlus size={16} /> Register Candidate</Button>
        </Link>
        {tab === "staff" ? (
          <Button onClick={() => setStaffModal(true)} data-testid="add-staff-btn"><Plus size={16} /> Add User</Button>
        ) : (
          <Button onClick={() => { setEditOrg(null); setModalOpen(true); }} data-testid="add-org-btn">
            <Plus size={16} /> Add {ORG_LABELS[tab] || ""}
          </Button>
        )}
      </PageHeader>

      <div className="mb-6 flex gap-1 rounded-lg border border-slate-200 bg-white p-1" style={{ width: "fit-content" }}>
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`rounded-md px-4 py-2 text-sm font-semibold transition-colors ${tab === t.key ? "bg-navy text-white" : "text-slate-600 hover:bg-slate-100"}`}
            data-testid={`tab-${t.key}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <Card>
        {tab === "staff" ? (
          staff === null ? <div className="flex justify-center py-12"><Spinner /></div>
            : staff.length === 0 ? <EmptyState text="No staff users yet" />
            : <UsersTable users={staff} onChanged={load} />
        ) : orgs === null ? (
          <div className="flex justify-center py-12"><Spinner /></div>
        ) : orgs.length === 0 ? (
          <EmptyState text={`No ${(ORG_LABELS[tab] || tab).toLowerCase()}s created yet`} />
        ) : (
          <Table headers={[`${ORG_LABELS[tab] || ""} Name`, "Email", "Address", "Users", "Status", "Actions"]}>
            {orgs.map((o) => (
              <tr key={o.id} className="border-b border-slate-100 transition-colors hover:bg-slate-50" data-testid="org-row">
                <td className="px-4 py-3 font-semibold text-slate-900">{o.name}</td>
                <td className="px-4 py-3 text-slate-600">{o.email || "-"}</td>
                <td className="px-4 py-3 text-slate-600">{o.address || "-"}</td>
                <td className="px-4 py-3 text-slate-600">{o.user_count}</td>
                <td className="px-4 py-3"><Badge color={o.status === "active" ? "green" : "red"}>{o.status}</Badge></td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-1">
                    <button onClick={() => { setEditOrg(o); setModalOpen(true); }} className="rounded p-1.5 text-slate-500 transition-colors hover:bg-slate-100 hover:text-navy" title="Edit" data-testid={`edit-org-${o.id}`}>
                      <Pencil size={15} />
                    </button>
                    <button onClick={() => toggleOrg(o)} className="rounded p-1.5 text-slate-500 transition-colors hover:bg-amber-50 hover:text-amber-600" title={o.status === "active" ? "Deactivate" : "Activate"} data-testid={`toggle-org-${o.id}`}>
                      <Power size={15} />
                    </button>
                    <Link to={`/admin/organizations/${o.id}`} className="ml-2 flex items-center gap-1 text-sm font-semibold text-teal transition-colors hover:text-navy" data-testid={`manage-org-${o.id}`}>
                      Manage <ArrowRight size={13} />
                    </Link>
                    <button onClick={() => removeOrg(o)} className="rounded p-1.5 text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600" title="Delete organization" data-testid={`delete-org-${o.id}`}>
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Card>

      <OrgModal open={modalOpen} onClose={() => setModalOpen(false)} type={tab} org={editOrg} onSaved={load} />
      <StaffUserModal open={staffModal} onClose={() => setStaffModal(false)} onSaved={load} />
    </div>
  );
}
